import React, { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { FileUp, CheckCircle2, AlertTriangle, Mail, Loader2 } from 'lucide-react';

const ukDate = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

const API = process.env.REACT_APP_BACKEND_URL;
const STAGE_COPY = {
  awaiting_certificate: { label: 'Upload new certificate', tone: 'bg-[#FFCB00]/20 text-[#8a6d00]' },
  awaiting_service: { label: 'Serve tenant', tone: 'bg-[#E2445C]/10 text-[#c83248]' },
  awaiting_tenancy: { label: 'Awaiting tenancy to serve', tone: 'bg-[#FFCB00]/20 text-[#8a6d00]' },
  closed: { label: 'Renewal closed', tone: 'bg-[#00CA72]/10 text-[#009e5a]' },
};

export function closeoutStageFor(r) {
  if (r.is_compliance !== true || r.status !== 'completed') return null;
  return r.compliance_closeout_stage || 'awaiting_certificate';
}

// Card-level prompt shown on a completed compliance job until it's closed.
export function ComplianceCloseoutPrompt({ r, onChanged }) {
  const [open, setOpen] = useState(false);
  const stage = closeoutStageFor(r);
  if (!stage) return null;
  const copy = STAGE_COPY[stage];
  return (
    <div onClick={(e) => e.stopPropagation()} className="mt-2">
      <button
        type="button"
        data-testid={`closeout-open-${r.id}`}
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-bold transition-colors hover:brightness-95 ${copy.tone}`}
      >
        {stage === 'closed' ? <CheckCircle2 size={14} /> : <FileUp size={14} />}
        {copy.label}
      </button>
      {open && (
        <ComplianceCloseoutModal mrId={r.id} onClose={() => setOpen(false)}
          onChanged={(s) => onChanged && onChanged({ compliance_closeout_stage: s })} />
      )}
    </div>
  );
}

export function ComplianceCloseoutModal({ mrId, onClose, onChanged }) {
  const [state, setState] = useState(null);
  const [extract, setExtract] = useState(null);
  const [form, setForm] = useState({ expiry_date: '', issue_date: '', issuing_company: '' });
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const res = await fetch(`${API}/api/compliance/closeout/${mrId}`, { credentials: 'include' });
    if (res.ok) setState(await res.json());
    else setError('Could not load this job.');
  }, [mrId]);
  useEffect(() => { load(); }, [load]);

  const apply = (d) => { setState(d); onChanged && onChanged(d.stage); };

  const upload = async (file) => {
    if (!file) return;
    setBusy('upload'); setError('');
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await fetch(`${API}/api/compliance/closeout/${mrId}/upload`, { method: 'POST', body: fd, credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Upload failed');
      setExtract(d);
      setForm({ expiry_date: d.expiry_date || '', issue_date: d.issue_date || '', issuing_company: d.issuing_company || '' });
    } catch (e) { setError(e.message); } finally { setBusy(''); }
  };

  const post = async (path, body) => {
    setBusy(path); setError('');
    try {
      const res = await fetch(`${API}/api/compliance/closeout/${mrId}/${path}`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Something went wrong');
      apply(d);
      if (d.serve && !d.serve.served && d.serve.detail) setError(d.serve.detail);
    } catch (e) { setError(e.message); } finally { setBusy(''); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg font-manrope" data-testid="closeout-modal">
        <DialogHeader>
          <DialogTitle className="font-satoshi text-xl text-[#323338]">
            {state ? `${state.doc_label} renewal` : 'Renewal close-out'}
          </DialogTitle>
          <DialogDescription className="text-[#676879]">
            {state?.property}{state?.contractor_ref ? ` · ${state.contractor_ref}` : ''}
          </DialogDescription>
        </DialogHeader>
        {!state && !error && <Loader2 className="animate-spin text-[#0073EA]" />}
        {state && <Steps stage={state.stage} />}
        {state?.stage === 'awaiting_certificate' && !extract && (
          <label data-testid="closeout-file-drop"
            className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-[#C3C6D4] bg-[#F8F8FA] px-6 py-8 text-center transition-colors hover:border-[#0073EA]">
            {busy === 'upload' ? <Loader2 className="animate-spin text-[#0073EA]" /> : <FileUp className="text-[#0073EA]" />}
            <span className="text-sm font-semibold text-[#323338]">
              {busy === 'upload' ? 'Reading the certificate…' : 'Upload the new certificate'}
            </span>
            <span className="text-xs text-[#676879]">PDF, JPG or PNG · we read the type and expiry for you</span>
            <input type="file" accept="application/pdf,image/jpeg,image/png" className="hidden"
              data-testid="closeout-file-input" onChange={(e) => upload(e.target.files[0])} />
          </label>
        )}
        {state?.stage === 'awaiting_certificate' && extract && (
          <ConfirmForm extract={extract} state={state} form={form} setForm={setForm} busy={busy}
            onBack={() => setExtract(null)}
            onConfirm={() => post('confirm', { upload_id: extract.upload_id, ...form,
              issue_date: form.issue_date || null, issuing_company: form.issuing_company || null })} />
        )}
        {state?.stage === 'awaiting_service' && (
          <div className="flex flex-col gap-3" data-testid="closeout-serve-panel">
            <p className="text-sm text-[#323338]">
              The new certificate is recorded (valid until <b>{ukDate(state.certificate?.expiry_date)}</b>) but the tenant
              hasn't been served a copy yet.
            </p>
            {state.last_serve_error && <ErrorLine text={state.last_serve_error} />}
            <button type="button" disabled={!!busy} data-testid="closeout-serve-btn" onClick={() => post('serve')}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#0073EA] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#0060c0] disabled:opacity-60">
              <Mail size={16} /> {busy ? 'Sending…' : `Email the certificate to ${state.tenant?.email || 'the tenant'}`}
            </button>
          </div>
        )}
        {state?.stage === 'awaiting_tenancy' && (
          <div className="flex flex-col gap-3" data-testid="closeout-tenancy-panel">
            <p className="text-sm text-[#323338]">
              The new certificate is recorded (valid until <b>{ukDate(state.certificate?.expiry_date)}</b>), but the
              property has no current tenant. It must be served on the next tenant before they move in, so this job
              stays open until it has been.
            </p>
            <button type="button" disabled={!!busy} data-testid="closeout-serve-tenancy-btn" onClick={() => post('serve')}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#0073EA] px-4 py-2.5 text-sm font-bold text-[#0073EA] transition-colors hover:bg-[#0073EA]/5 disabled:opacity-60">
              <Mail size={16} /> {busy ? 'Checking…' : 'A tenancy has started — serve now'}
            </button>
          </div>
        )}
        {state?.stage === 'closed' && (
          <div className="flex flex-col gap-2 rounded-xl bg-[#00CA72]/10 p-4" data-testid="closeout-closed-panel">
            <span className="flex items-center gap-2 text-sm font-bold text-[#009e5a]"><CheckCircle2 size={16} /> Renewal closed</span>
            <span className="text-sm text-[#323338]">{state.closed_note}</span>
            {state.certificate && (
              <a className="text-sm font-semibold text-[#0073EA] hover:underline" target="_blank" rel="noreferrer"
                href={`${API}/api/compliance/doc/${state.certificate.id}/file`} data-testid="closeout-view-cert">
                View certificate (valid until {ukDate(state.certificate.expiry_date)})
              </a>
            )}
          </div>
        )}
        {error && <ErrorLine text={error} testid="closeout-error" />}
      </DialogContent>
    </Dialog>
  );
}

function Steps({ stage }) {
  const order = ['awaiting_certificate', 'awaiting_service', 'closed'];
  const names = ['Certificate', 'Tenant served', 'Closed'];
  const at = order.indexOf(stage === 'awaiting_tenancy' ? 'awaiting_service' : stage);
  return (
    <ol className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest" data-testid="closeout-steps">
      {names.map((n, i) => (
        <li key={n} className={`flex items-center gap-2 ${i < at || stage === 'closed' ? 'text-[#009e5a]' : i === at ? 'text-[#0073EA]' : 'text-[#C3C6D4]'}`}>
          <span className={`h-2 w-2 rounded-full ${i < at || stage === 'closed' ? 'bg-[#00CA72]' : i === at ? 'bg-[#0073EA]' : 'bg-[#C3C6D4]'}`} />
          {n}{i < names.length - 1 && <span className="mx-1 h-px w-6 bg-[#E6E9EF]" />}
        </li>
      ))}
    </ol>
  );
}

function ConfirmForm({ extract, state, form, setForm, busy, onBack, onConfirm }) {
  const field = 'w-full rounded-lg border border-[#E6E9EF] px-3 py-2 text-sm text-[#323338] focus:border-[#0073EA] focus:outline-none';
  return (
    <div className="flex flex-col gap-3" data-testid="closeout-confirm-form">
      {extract.extraction_error && <ErrorLine text={extract.extraction_error} />}
      {extract.type_mismatch && (
        <ErrorLine testid="closeout-type-mismatch"
          text={`This looks like a ${extract.ai_doc_type?.replace('_', ' ')} certificate, but the job is a ${state.doc_label} renewal. Check you uploaded the right file.`} />
      )}
      <label className="text-[11px] font-bold uppercase tracking-widest text-[#676879]">New expiry date
        <input type="date" className={`${field} mt-1`} value={form.expiry_date} data-testid="closeout-expiry-input"
          onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} />
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="text-[11px] font-bold uppercase tracking-widest text-[#676879]">Issue date
          <input type="date" className={`${field} mt-1`} value={form.issue_date} data-testid="closeout-issue-input"
            onChange={(e) => setForm({ ...form, issue_date: e.target.value })} />
        </label>
        <label className="text-[11px] font-bold uppercase tracking-widest text-[#676879]">Issued by
          <input className={`${field} mt-1`} value={form.issuing_company} data-testid="closeout-company-input"
            onChange={(e) => setForm({ ...form, issuing_company: e.target.value })} />
        </label>
      </div>
      <p className="text-xs text-[#676879]">
        Saving replaces the old certificate (expiring {ukDate(state.old_expiry_date)}) and emails a copy to
        {' '}{state.tenant?.email || 'the tenant'}{state.tenant ? '' : ' — no current tenant, so the job closes without serving'}.
      </p>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onBack} data-testid="closeout-back-btn"
          className="rounded-lg border border-[#E6E9EF] px-4 py-2 text-sm font-semibold text-[#676879] transition-colors hover:bg-[#F4F5F7]">
          Choose another file
        </button>
        <button type="button" disabled={!form.expiry_date || !!busy} onClick={onConfirm} data-testid="closeout-confirm-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-[#0073EA] px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#0060c0] disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />} Save and serve tenant
        </button>
      </div>
    </div>
  );
}

function ErrorLine({ text, testid }) {
  return (
    <p data-testid={testid} className="flex items-start gap-2 rounded-lg bg-[#E2445C]/10 px-3 py-2 text-sm text-[#c83248]">
      <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {text}
    </p>
  );
}
